import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  LightbulbOutlined,
  Close,
  CloudUpload,
  AutoAwesome,
  CheckCircle,
} from '@mui/icons-material';
import { DISMISSED_GUIDE_KEY } from '../../utils/storageKeys';

interface GettingStartedGuideProps {
  totalBatches: number;
  completedBatches: number;
  readyForReview: number;
  onStartTransformation: () => void;
}



const GettingStartedGuide: React.FC<GettingStartedGuideProps> = ({
  totalBatches,
  completedBatches,
  readyForReview,
  onStartTransformation,
}) => {
  const theme = useTheme();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_GUIDE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Don't show if user is experienced or dismissed
  if (dismissed || completedBatches >= 5) return null;

  // Only show full guide if no batches, else show mini tip
  const isNewUser = totalBatches === 0;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_GUIDE_KEY, '1');
    } catch {
      // localStorage may be unavailable
    }
  };

  if (!isNewUser) {
    if (readyForReview <= 0) return null;
    // Mini tip for users with some batches
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 4,
          borderLeft: `4px solid ${theme.palette.secondary.main}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <LightbulbOutlined sx={{ color: 'secondary.main' }} />
          <Typography variant="body2" color="text.secondary">
            You have transformations waiting for review. Click "Review Mapping" to check the AI suggestions.
          </Typography>
        </Stack>
        <IconButton size="small" onClick={handleDismiss}>
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  // Full guide for new users
  const steps = [
    {
      icon: <CloudUpload sx={{ color: 'primary.main' }} />,
      title: 'Upload',
      desc: 'Drop your CSV, Excel, or JSON files',
    },
    {
      icon: <AutoAwesome sx={{ color: 'secondary.main' }} />,
      title: 'Review',
      desc: 'AI maps your fields to the target format',
    },
    {
      icon: <CheckCircle sx={{ color: 'success.main' }} />,
      title: 'Transform',
      desc: 'Approve and get your clean data out',
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        mb: 4,
        borderLeft: `4px solid ${theme.palette.secondary.main}`,
        position: 'relative',
      }}
    >
      <IconButton
        size="small"
        onClick={handleDismiss}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <Close fontSize="small" />
      </IconButton>

      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <LightbulbOutlined sx={{ color: 'secondary.main' }} />
        <Typography variant="subtitle1" fontWeight={600}>
          Getting Started
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Transform your data in three simple steps:
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} mb={3}>
        {steps.map((step, i) => (
          <Box
            key={step.title}
            sx={{
              flex: 1,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.default, 1),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'primary.main',
                }}
              >
                {i + 1}
              </Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {step.title}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 5.5 }}>
              {step.desc}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Button
        variant="contained"
        startIcon={<CloudUpload />}
        onClick={onStartTransformation}
      >
        Start Your First Transformation
      </Button>
    </Paper>
  );
};

export default GettingStartedGuide;
